import React, { useEffect, useRef, useState } from "react";

import { Moment } from "moment";

import { formatNumericCurrency, useTranslation } from "utils";

import { CURRENCY_CODE, CurrencyCode } from "consts";

import { PRACTICE_SLUG, PracticeSlug } from "models";

import { Flex, Icon, Tag, TooltipWrapper } from "components";

import { FertilizerValue, PRACTICE_TYPE, SeedingValue, TillageValue } from "pages/app/field-timeline/components/FieldDetailsTimeline.tsx";

export const PracticeCard = ({
  startTime,
  endTime,
  type,
  valuesToDisplay,
  currency = CURRENCY_CODE.EUR,
}: {
  startTime: Moment;
  endTime: Moment;
  type: PRACTICE_TYPE;
  valuesToDisplay?: TillageValue[] | FertilizerValue[] | SeedingValue[];
  currency?: CurrencyCode;
}) => {
  const { t } = useTranslation("fieldTimeline");
  const cardRef = useRef<HTMLDivElement | null>(null);
  const nameContainerRefs = useRef<(HTMLParagraphElement | null)[]>([]);
  const nameRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const [shouldShowTooltips, setShouldShowTooltips] = useState<Record<number, boolean>>({});
  const [isMediumView, setIsMediumView] = useState(false);
  const [isSmallView, setIsSmallView] = useState(false);
  // We are only showing the first 3 items. All the remaining items will be displayed in a tooltip when hovering on the "...".
  const AMOUNT_OF_ITEMS_TO_DISPLAY = 3;
  const remainingItems = valuesToDisplay?.slice(AMOUNT_OF_ITEMS_TO_DISPLAY);

  const getIcon = (type: PRACTICE_TYPE, practiceType?: PracticeSlug) => {
    switch (type) {
      case PRACTICE_TYPE.TILLAGE:
        return "klim/tillage";
      case PRACTICE_TYPE.FERTILIZER:
        if (practiceType === PRACTICE_SLUG.MINERAL_FERTILIZERS) return "klim/fertiliser";
        if (practiceType === PRACTICE_SLUG.ORGANIC_FERTILIZERS) return "klim/compost";
        return "klim/fertiliser";
      case PRACTICE_TYPE.SEEDING:
        return "klim/planting-seeds";
      default:
        return "";
    }
  };

  useEffect(() => {
    const cardElement = cardRef.current;
    setIsSmallView(false);
    setIsMediumView(false);

    if (!cardElement) return;

    // This is needed for smaller screens
    if (cardElement.offsetWidth < 75) {
      setIsSmallView(true);
      return;
    }

    const timeDiff = endTime.diff(startTime, "weeks");

    if (timeDiff <= 4) {
      setIsSmallView(true);
      return;
    }
    if (timeDiff > 4 && timeDiff < 14) {
      setIsMediumView(true);
    }
  }, [valuesToDisplay, startTime, endTime]);

  useEffect(() => {
    if (!valuesToDisplay) return;

    const newTooltipState: Record<number, boolean> = {};

    valuesToDisplay.forEach((_, index) => {
      const nameContainerElement = nameContainerRefs.current[index];
      const nameElement = nameRefs.current[index];

      if (!nameContainerElement || !nameElement) return;

      newTooltipState[index] = nameElement.offsetWidth > nameContainerElement.offsetWidth;
    });

    setShouldShowTooltips(newTooltipState);
  }, [valuesToDisplay, startTime, endTime]);

  const remainingItemsTooltipContainer = (isBaseline: boolean, isConfirmed: boolean) => (
    <Flex vertical={true} gap="0.25rem">
      {isBaseline && <p className="paragraph-m margin-0 white-text">{t("cropCard.baseline")}</p>}
      {isConfirmed && <p className="paragraph-m margin-0 white-text">{t("cropCard.submitted")}</p>}
      {remainingItems?.map((remainingItem, index) => (
        <p className="paragraph-m margin-0 white-text" key={index}>
          {remainingItem.name}
        </p>
      ))}
    </Flex>
  );

  const singleItemTooltipContainer = (name: string, isBaseline: boolean, isConfirmed: boolean) => (
    <Flex vertical={true} gap="0.25rem">
      {isBaseline && <p className="paragraph-m margin-0 white-text">{t("cropCard.baseline")}</p>}
      {isConfirmed && <p className="paragraph-m margin-0 white-text">{t("cropCard.submitted")}</p>}
      <p className="paragraph-m margin-0 white-text">{name}</p>
    </Flex>
  );

  const smallView = () =>
    valuesToDisplay?.map((value, index) => {
      if (index > AMOUNT_OF_ITEMS_TO_DISPLAY) return null;
      const practiceType = type === PRACTICE_TYPE.SEEDING ? undefined : (value as FertilizerValue | TillageValue).type;

      return (
        <Flex data-testid={"practice-card-small"} key={index} align="center" justify="center" gap="0.5rem">
          {index === 0 ? (
            <TooltipWrapper tooltip={{ title: singleItemTooltipContainer(value.name, value.isBaseline, value.isConfirmed), placement: "top" }}>
              <Icon iconName={getIcon(type, practiceType)} className="practice-icon" color="black" />
            </TooltipWrapper>
          ) : (
            <TooltipWrapper
              tooltip={{
                title:
                  index === AMOUNT_OF_ITEMS_TO_DISPLAY
                    ? remainingItemsTooltipContainer(value.isBaseline, value.isConfirmed)
                    : singleItemTooltipContainer(value.name, value.isBaseline, value.isConfirmed),
                placement: "top",
              }}
            >
              <p className="paragraph-m margin-0">...</p>
            </TooltipWrapper>
          )}
        </Flex>
      );
    });

  const mediumView = () =>
    valuesToDisplay?.map((value, index) => {
      if (index > AMOUNT_OF_ITEMS_TO_DISPLAY) return null;
      const showIcons = index < AMOUNT_OF_ITEMS_TO_DISPLAY;
      const showTooltip = shouldShowTooltips[index] || index === AMOUNT_OF_ITEMS_TO_DISPLAY;

      const practiceType = type === PRACTICE_TYPE.SEEDING ? undefined : (value as FertilizerValue | TillageValue).type;

      return (
        <Flex data-testid={"practice-card-medium"} key={index} align="center" justify="space-between" gap="0.5rem">
          <Icon
            iconName={getIcon(type, practiceType)}
            className="practice-icon"
            color="black"
            style={{ visibility: `${index === 0 ? "visible" : "hidden"}` }}
          />
          {!value.payout && (
            <p className="name-container" ref={(el) => (nameContainerRefs.current[index] = el)}>
              <TooltipWrapper
                tooltip={
                  showTooltip
                    ? {
                        title:
                          index === AMOUNT_OF_ITEMS_TO_DISPLAY
                            ? remainingItemsTooltipContainer(value.isBaseline, value.isConfirmed)
                            : singleItemTooltipContainer(value.name, value.isBaseline, value.isConfirmed),
                        placement: "bottom",
                      }
                    : {}
                }
              >
                <span data-testid={`practice-card-large-item-${index}-name`} className="name" ref={(el) => (nameRefs.current[index] = el)}>
                  {index === AMOUNT_OF_ITEMS_TO_DISPLAY ? "..." : value.name}
                </span>
              </TooltipWrapper>
            </p>
          )}
          {showIcons && (
            <>
              {value.payout && value.payout > 0 ? (
                <span className="payout-digits" style={{ visibility: `${index < AMOUNT_OF_ITEMS_TO_DISPLAY ? "visible" : "hidden"}` }}>
                  {formatNumericCurrency(value.payout, { decimals: 0, currency })}
                </span>
              ) : null}
            </>
          )}
        </Flex>
      );
    });

  const largeView = () =>
    valuesToDisplay?.map((value, index) => {
      if (index > AMOUNT_OF_ITEMS_TO_DISPLAY) return null;
      const showIcons = index < AMOUNT_OF_ITEMS_TO_DISPLAY;
      const showTooltip = shouldShowTooltips[index] || index === AMOUNT_OF_ITEMS_TO_DISPLAY;

      const practiceType = type === PRACTICE_TYPE.SEEDING ? undefined : (value as FertilizerValue | TillageValue).type;

      return (
        <Flex data-testid={"practice-card-large"} key={index} align="center" gap="0.5rem">
          <Icon
            iconName={getIcon(type, practiceType)}
            className="practice-icon"
            color="black"
            style={{ visibility: `${index === 0 ? "visible" : "hidden"}` }}
          />
          <p className="name-container" ref={(el) => (nameContainerRefs.current[index] = el)}>
            <TooltipWrapper
              tooltip={
                showTooltip
                  ? {
                      title:
                        index === AMOUNT_OF_ITEMS_TO_DISPLAY
                          ? remainingItemsTooltipContainer(value.isBaseline, value.isConfirmed)
                          : singleItemTooltipContainer(value.name, value.isBaseline, value.isConfirmed),
                      placement: "bottom",
                    }
                  : {}
              }
            >
              <span data-testid={`practice-card-large-item-${index}-name`} className="name" ref={(el) => (nameRefs.current[index] = el)}>
                {index === AMOUNT_OF_ITEMS_TO_DISPLAY ? "..." : value.name}
              </span>
            </TooltipWrapper>
          </p>
          {showIcons && (
            <>
              {value.payout && value.payout > 0 ? (
                <span className="payout-digits" style={{ visibility: `${index < AMOUNT_OF_ITEMS_TO_DISPLAY ? "visible" : "hidden"}` }}>
                  {formatNumericCurrency(value.payout, { decimals: 0, currency })}
                </span>
              ) : null}
              {value.isBaseline && <Tag className="baseline-tag" colour="grey" text={t("cropCard.baseline")} />}
              {value.isConfirmed && (
                <Icon dataTestId={`practice-card-large-item-${index}-lock-icon`} className="confirmed-icon" iconName="tabler/lock" size="s" color="black" />
              )}
            </>
          )}
        </Flex>
      );
    });

  const renderView = () => {
    if (isSmallView) return smallView();
    if (isMediumView) return mediumView();
    return largeView();
  };

  return (
    <div data-testid={`practice-card-${type}`} className="practice-card" ref={cardRef}>
      {renderView()}
    </div>
  );
};
